"use client"

import type { VariantProps } from "class-variance-authority"
import type { ComponentProps, FC, ReactNode, SVGProps } from "react"
import { forwardRef, memo, useLayoutEffect, useRef, useState } from "react"
import { AnimatePresence, motion } from "motion/react"
import { IconCircleWarningOutline18 as AlertCircleIcon } from "nucleo-ui-outline-18"

import { cn } from "@productlane/lib/src/browser"

import type { IconType } from "../../../icons"
import { Label } from "../label"
import { inputVariants } from "./input-variants"

// HTML autocomplete attribute values for form inputs
// https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete
type AutoCompleteValue =
	| "off"
	| "on"
	| "name"
	| "honorific-prefix"
	| "given-name"
	| "additional-name"
	| "family-name"
	| "honorific-suffix"
	| "nickname"
	| "email"
	| "username"
	| "new-password"
	| "current-password"
	| "one-time-code"
	| "organization-title"
	| "organization"
	| "street-address"
	| "address-line1"
	| "address-line2"
	| "address-line3"
	| "address-level4"
	| "address-level3"
	| "address-level2"
	| "address-level1"
	| "country"
	| "country-name"
	| "postal-code"
	| "cc-name"
	| "cc-given-name"
	| "cc-additional-name"
	| "cc-family-name"
	| "cc-number"
	| "cc-exp"
	| "cc-exp-month"
	| "cc-exp-year"
	| "cc-csc"
	| "cc-type"
	| "transaction-currency"
	| "transaction-amount"
	| "language"
	| "bday"
	| "bday-day"
	| "bday-month"
	| "bday-year"
	| "sex"
	| "tel"
	| "tel-country-code"
	| "tel-national"
	| "tel-area-code"
	| "tel-local"
	| "tel-local-prefix"
	| "tel-local-suffix"
	| "tel-extension"
	| "impp"
	| "url"
	| "photo"

export interface InputProps
	extends
		Omit<ComponentProps<"input">, "autoComplete" | "prefix">,
		VariantProps<typeof inputVariants> {
	autoComplete?: AutoCompleteValue
	label?: ReactNode
	name: string
	inputClassName?: string
	suffix?: ReactNode
	hint?: ReactNode
	errorMessage?: ReactNode
	showErrorMessage?: boolean
	icon?: LucideIcon | IconType
	iconClassName?: string
	iconPosition?: "left" | "right"
	iconOnClick?: () => void
	truncate?: boolean
	isRequired?: boolean
	prefix?: ReactNode
	postfix?: string
	allowPasswordManager?: boolean
}

export const Input = memo(
	forwardRef<HTMLInputElement, InputProps>(
		(
			{
				className,
				label,
				inputClassName,
				name,
				suffix,
				hint,
				errorMessage,
				showErrorMessage = true,
				type = "text",
				icon: Icon,
				readOnly,
				iconPosition = "left",
				iconClassName,
				iconOnClick,
				truncate,
				prefix,
				variant,
				textSize,
				postfix,
				allowPasswordManager = false,
				autoComplete = "off",
				...props
			},
			ref,
		) => {
			const prefixRef = useRef<HTMLSpanElement>(null)
			const [prefixWidth, setPrefixWidth] = useState(0)

			useLayoutEffect(() => {
				setPrefixWidth(prefix ? (prefixRef.current?.offsetWidth ?? 0) : 0)
			}, [prefix])

			return (
				<div className={cn("flex w-full flex-col", className)}>
					{label && (
						<Label className="mb-1.5" htmlFor={name}>
							{label}
						</Label>
					)}
					<div className="flex flex-row gap-3">
						<div
							className={cn("inline-flex flex-1", {
								"shadow-sm dark:shadow-black": variant === "outline",
							})}
						>
							<div className="relative flex-1">
								{Icon && (
									<Icon
										strokeWidth={1.75}
										onClick={(e) => {
											e.preventDefault()
											iconOnClick && iconOnClick()
										}}
										className={cn(
											"absolute top-1/2 h-4 w-4 -translate-y-1/2 cursor-default text-gray-400 transition-all",
											iconPosition === "left" ? "left-2" : "right-2",
											iconOnClick
												? "hover:text-gray-500 dark:hover:text-gray-300"
												: "pointer-events-none",
											iconClassName,
										)}
									/>
								)}
								{prefix && (
									<span
										ref={prefixRef}
										className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-tertiary"
									>
										{prefix}
									</span>
								)}
								<input
									style={
										prefixWidth ? { paddingLeft: prefixWidth + 14 } : undefined
									}
									className={cn(
										inputVariants({ variant, textSize }),
										(suffix ?? errorMessage) ? "pr-9" : "pr-3",
										Icon && iconPosition === "left" && "pl-9",
										Icon && iconPosition === "right" && "pr-9",
										truncate && "truncate",
										readOnly &&
											"cursor-default !text-opacity-70 ring-gray-900/15 focus:!outline-none focus:ring-gray-900/15 dark:!text-opacity-70 dark:ring-white/10 dark:focus:!outline-none dark:focus:ring-white/20",
										!errorMessage &&
											!readOnly &&
											"dark:focus:ring-white-30 ring-gray-900/15  focus:ring-black/30 dark:ring-white/15 focus:dark:ring-white/30",
										Boolean(errorMessage) &&
											"!ring-red-300 focus:!ring-red-600/80 dark:!ring-red-500/50 dark:focus:!ring-red-500",
										inputClassName,
									)}
									ref={ref}
									name={name}
									id={name}
									readOnly={readOnly}
									aria-invalid={!!errorMessage}
									aria-describedby={`${name}-error`}
									type={type}
									autoComplete={autoComplete}
									{...(allowPasswordManager
										? { "data-allow-autofill": "true" }
										: {
												"data-1p-ignore": "true",
												"data-lpignore": "true",
												"data-protonpass-ignore": "true",
											})}
									{...props}
								/>

								{!!postfix && (
									<span className="absolute right-0 top-1/2 -translate-y-1/2 text-tertiary">
										{postfix}
									</span>
								)}

								{(suffix ?? errorMessage) && (
									<div
										className={cn(
											"absolute inset-y-0 right-0 flex items-center pr-2",
											// `errorMessage` is a static icon — leave it
											// non-interactive so it doesn't intercept input
											// focus clicks. Custom `suffix` nodes (e.g. copy
											// buttons, dropdowns) need pointer events to work.
											errorMessage
												? "pointer-events-none text-red-500"
												: "text-secondary",
										)}
									>
										{errorMessage ? (
											<AlertCircleIcon className="h-4 w-4" />
										) : (
											suffix
										)}
									</div>
								)}
							</div>
						</div>
					</div>
					{hint && (
						<div className={cn("mt-1.5 text-left text-xs text-secondary")}>
							{hint}
						</div>
					)}
					<AnimatePresence>
						{errorMessage && showErrorMessage && (
							<motion.p
								initial={{
									opacity: 0,
									height: 0,
									marginTop: 0,
								}}
								animate={{
									opacity: 1,
									height: "auto",
									marginTop: "0.5rem",
								}}
								exit={{
									opacity: 0,
									height: 0,
									marginTop: 0,
								}}
								transition={{ duration: 0.1 }}
								className="mt-1.5 text-start text-sm text-red-600"
								id={`${name}-error`}
							>
								{errorMessage}
							</motion.p>
						)}
					</AnimatePresence>
				</div>
			)
		},
	),
)

type LucideIcon = FC<SVGProps<SVGSVGElement>>
